/**
 * Gap Adapter
 * Transforms the gaps wire shape into the board's domain entities.
 * Every assumption about the contract lives here and nowhere else.
 */

import { BaseAdapter } from './base.adapter';
import {
  GAP_FIX_STATUSES,
  GAP_FIX_TARGET_TYPES,
  GAP_PRIORITY_BANDS,
  GAP_RUN_CASE_KINDS,
  GAP_RUN_ROW_STATUSES,
  GAP_RUN_SET_STATUSES,
  GAP_RUN_VERDICTS,
  GAP_SEVERITY_LEVELS,
  GAP_STATUSES,
  GAP_TICKET_STATUSES,
  RUNG_ORDER,
  hasOutcomeEvidence,
} from '@/modules/gaps/utils';
import type {
  Gap,
  GapAction,
  GapActionDto,
  GapActionRequestDto,
  GapActionType,
  GapDetail,
  GapDetailDto,
  GapDto,
  GapFixStatus,
  GapFixTargetLink,
  GapFixTargetLinkKind,
  GapFixTargetRef,
  GapFixTargetType,
  GapGroundTruth,
  GapGroundTruthDto,
  GapIntentsDto,
  GapListDto,
  GapPage,
  GapPriority,
  GapPriorityBand,
  GapPriorityDto,
  GapRegressedGap,
  GapRegressedGapDto,
  GapRunCaseKind,
  GapRunDto,
  GapRunProgressDto,
  GapRunRow,
  GapRunRowDto,
  GapRunRowStatus,
  GapRunSet,
  GapRunSetDetail,
  GapRunSetDetailDto,
  GapRunSetDto,
  GapRunSetStatus,
  GapRunSetTotals,
  GapRunSetTotalsDto,
  GapRunSetUser,
  GapRunSetUserDto,
  GapRunVerdict,
  GapSeverity,
  GapSeverityDto,
  GapSeverityLevel,
  GapStatus,
  GapStatusEvent,
  GapStatusEventDto,
  GapSuggestedFix,
  GapWorldPatchEntry,
  GapWorldPatchStatus,
  GapSuggestedFixDto,
  GapThread,
  GapThreadBlueprint,
  GapThreadBlueprintDto,
  GapThreadDto,
  GapThreadFeedback,
  GapThreadFeedbackDto,
  GapThreadMessage,
  GapThreadMessageDto,
  GapThreadRefine,
  GapThreadRefineDto,
  GapThreadResponse,
  GapThreadResponseDto,
  GapThreadRevisionCause,
  GapThreadTicket,
  GapThreadTicketDto,
  GapThreadTurn,
  GapThreadTurnDto,
  GapThreadTurnEnd,
  GapTicketStatus,
  GapTraceRef,
  GapTraceRefDto,
  LadderRung,
} from '@/modules/gaps/types';

const ACTION_TYPES: readonly GapActionType[] = [
  'ack',
  'dispute',
  'mark_fixed',
  'note',
  'route_tech',
  'unroute_tech',
  'reopen',
  'fix_resolve',
  'fix_ignore',
  'fix_route_tech',
  'fix_reopen',
  'fix_patch_approve',
  'fix_patch_reject',
  // System-only — see the `GapActionType` doc comment in `types.ts`.
  'test_passed',
  'test_failed',
  'pass_revoked',
];

function toLadderRung(value: string | null): LadderRung | null {
  if (!value) return null;
  return RUNG_ORDER.includes(value as LadderRung) ? (value as LadderRung) : null;
}

export function toGapStatus(value: string): GapStatus | null {
  return GAP_STATUSES.includes(value as GapStatus) ? (value as GapStatus) : null;
}

function toSeverityLevel(value: string | null): GapSeverityLevel | null {
  if (!value) return null;
  return GAP_SEVERITY_LEVELS.includes(value as GapSeverityLevel)
    ? (value as GapSeverityLevel)
    : null;
}

function toPriorityBand(value: string | null): GapPriorityBand | null {
  if (!value) return null;
  return GAP_PRIORITY_BANDS.includes(value as GapPriorityBand) ? (value as GapPriorityBand) : null;
}

function toFixTargetType(value: string | null): GapFixTargetType | null {
  if (!value) return null;
  return GAP_FIX_TARGET_TYPES.includes(value as GapFixTargetType) ? (value as GapFixTargetType) : null;
}

/**
 * The Tickets list has no "unknown" tag, so an absent or unrecognised wire
 * value falls back to `unresolved` rather than to null — a ticket is only
 * shown as resolved once the backend says so.
 */
function toTicketStatus(value: string | null | undefined): GapTicketStatus {
  if (!value) return 'unresolved';
  return GAP_TICKET_STATUSES.includes(value as GapTicketStatus)
    ? (value as GapTicketStatus)
    : 'unresolved';
}

function toFixStatus(value: string | null | undefined): GapFixStatus | null {
  if (!value) return null;
  return GAP_FIX_STATUSES.includes(value as GapFixStatus) ? (value as GapFixStatus) : null;
}

const GAP_WORLD_PATCH_STATUSES: readonly GapWorldPatchStatus[] = ['proposed', 'approved', 'rejected'];

function isWorldPatchEntry(value: unknown): value is GapWorldPatchEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  return typeof entry['tool'] === 'string' && typeof entry['path'] === 'string' && typeof entry['evidence'] === 'string';
}

/** The patch's well-formed entries, or null when there are none (a malformed entry is dropped). */
function toWorldPatch(value: unknown): GapWorldPatchEntry[] | null {
  if (!Array.isArray(value)) return null;
  const entries = value
    .filter(isWorldPatchEntry)
    .map((entry) => ({ tool: entry.tool, path: entry.path, value: entry.value ?? null, evidence: entry.evidence }));
  return entries.length > 0 ? entries : null;
}

/** A patch with no recorded decision is proposed; no patch means no status at all. */
function toWorldPatchStatus(value: string | null | undefined, hasPatch: boolean): GapWorldPatchStatus | null {
  if (!hasPatch) return null;
  return GAP_WORLD_PATCH_STATUSES.includes(value as GapWorldPatchStatus) ? (value as GapWorldPatchStatus) : 'proposed';
}

function toRegressionSource(value: string | null | undefined): 'related_gap' | 'passing_ticket' | null {
  return value === 'related_gap' || value === 'passing_ticket' ? value : null;
}

function toRunSetStatus(value: string): GapRunSetStatus | null {
  return GAP_RUN_SET_STATUSES.includes(value as GapRunSetStatus) ? (value as GapRunSetStatus) : null;
}

function toRunRowStatus(value: string): GapRunRowStatus | null {
  return GAP_RUN_ROW_STATUSES.includes(value as GapRunRowStatus) ? (value as GapRunRowStatus) : null;
}

function toRunVerdict(value: string | null): GapRunVerdict | null {
  if (!value) return null;
  return GAP_RUN_VERDICTS.includes(value as GapRunVerdict) ? (value as GapRunVerdict) : null;
}

function toRunCaseKind(value: string): GapRunCaseKind | null {
  return GAP_RUN_CASE_KINDS.includes(value as GapRunCaseKind) ? (value as GapRunCaseKind) : null;
}

/** `inputSource` is a later backend PR's field — anything else (absent, unrecognised) reads as null. */
function toRunRowInputSource(value: string | null | undefined): 'mirror' | 'langfuse' | null {
  return value === 'mirror' || value === 'langfuse' ? value : null;
}

/** `previousSource` is a later backend PR's field — anything else (absent, unrecognised) reads as null. */
function toRunRowPreviousSource(value: string | null | undefined): 'served' | 'previous_run' | null {
  return value === 'served' || value === 'previous_run' ? value : null;
}

/** True for a plain object (not an array, not null) — the shape every typed targetRef takes. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toSopTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || typeof value.sopId !== 'string') return null;
  return { sopId: value.sopId, quote: typeof value.quote === 'string' ? value.quote : null };
}

function toPersonaTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || typeof value.personaId !== 'string') return null;
  return { personaId: value.personaId };
}

function toKbTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || typeof value.kbEntryId !== 'string') return null;
  return { kbEntryId: value.kbEntryId };
}

function toWorkflowTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || typeof value.workflowId !== 'string') return null;
  return { workflowId: value.workflowId };
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/** A tool fix names its tool, and may name the provider the tool belongs to. */
function toToolTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || !isNonBlankString(value.toolName)) return null;
  return { toolName: value.toolName, provider: isNonBlankString(value.provider) ? value.provider : null };
}

function toIntegrationTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || !isNonBlankString(value.provider)) return null;
  return { provider: value.provider };
}

function toConfigurationTargetRef(value: unknown): GapFixTargetRef | null {
  if (!isPlainObject(value) || !isNonBlankString(value.settingKey)) return null;
  return { settingKey: value.settingKey };
}

/**
 * Validates `targetRef` against the shape its own `targetType` requires
 * (Brahma's `ladder.normalize_target_ref`) — mirrors `toFixTargetType` /
 * `toFixStatus`: anything that doesn't fit becomes `null` rather than being
 * passed through. Covers a bare legacy string (fails every shape check),
 * `test_set` (never carries a ref), `code` (its ref is not read) and a wrong-shaped object.
 */
function toFixTargetRef(targetType: GapFixTargetType | null, value: unknown): GapFixTargetRef | null {
  if (!targetType || value == null) return null;
  switch (targetType) {
    case 'sop':
      return toSopTargetRef(value);
    case 'persona':
      return toPersonaTargetRef(value);
    case 'kb':
      return toKbTargetRef(value);
    case 'workflow':
      return toWorkflowTargetRef(value);
    case 'tool':
      return toToolTargetRef(value);
    case 'integration':
      return toIntegrationTargetRef(value);
    case 'configuration':
      return toConfigurationTargetRef(value);
    case 'test_set':
    case 'code':
      return null;
  }
}

const FIX_TARGET_LINK_KINDS: readonly GapFixTargetLinkKind[] = ['protocol', 'sop', 'source', 'persona'];

/** The chip's link, only for a known page kind with an id — anything else leaves the chip unlinked. */
function toFixTargetLink(value: unknown): GapFixTargetLink | null {
  if (!isPlainObject(value) || typeof value.id !== 'string' || value.id.trim() === '') return null;
  const kind = FIX_TARGET_LINK_KINDS.find((known) => known === value.kind);
  return kind ? { kind, id: value.id } : null;
}

const THREAD_REVISION_CAUSES: readonly GapThreadRevisionCause[] = ['initial', 'reviewer_refine', 'auto_replan', 'other'];
const THREAD_TURN_ENDS: readonly GapThreadTurnEnd[] = ['sent', 'closed', 'unassigned', 'handed_off'];

function toRevisionCause(value: string | null | undefined): GapThreadRevisionCause {
  return THREAD_REVISION_CAUSES.find((cause) => cause === value) ?? 'other';
}

function toTurnEnd(value: string | null | undefined): GapThreadTurnEnd | null {
  return THREAD_TURN_ENDS.find((end) => end === value) ?? null;
}

export class GapAdapter extends BaseAdapter {
  /**
   * The writer stores fixes as `{text, traceId}`; bare strings exist in older documents.
   * Normalising here keeps the shape check out of the components.
   */
  static fromSuggestedFixesApi(
    fixes: (GapSuggestedFixDto | string)[] | null | undefined
  ): GapSuggestedFix[] {
    if (!Array.isArray(fixes)) return [];
    return fixes
      .map((fix) => {
        if (typeof fix === 'string') {
          return {
            text: fix,
            traceId: null,
            targetType: null,
            targetRef: null,
            targetName: null,
            targetLink: null,
            fixId: null,
            status: null,
            worldPatch: null,
            worldPatchStatus: null,
          };
        }
        const targetType = toFixTargetType(fix?.targetType ?? null);
        const worldPatch = toWorldPatch(fix?.worldPatch);
        return {
          text: String(fix?.text ?? ''),
          traceId: fix?.traceId ?? null,
          targetType,
          targetRef: toFixTargetRef(targetType, fix?.targetRef),
          targetName: typeof fix?.targetName === 'string' ? fix.targetName : null,
          targetLink: toFixTargetLink(fix?.targetLink),
          fixId: fix?.fixId ?? null,
          status: toFixStatus(fix?.status),
          worldPatch,
          worldPatchStatus: toWorldPatchStatus(fix?.worldPatchStatus, worldPatch !== null),
        };
      })
      .filter((fix) => fix.text.length > 0);
  }

  /**
   * Severity and priority are computed by the store and rendered verbatim — the
   * board never recomputes a rank. A gap written before the board carries
   * neither block, which reads as "unranked" rather than as a zero.
   */
  static fromSeverityApi(dto: GapSeverityDto | null | undefined): GapSeverity {
    return {
      level: toSeverityLevel(dto?.level ?? null),
      rawLevel: dto?.level ?? null,
      weight: dto?.weight ?? null,
      rule: dto?.rule ?? null,
    };
  }

  static fromPriorityApi(dto: GapPriorityDto | null | undefined): GapPriority {
    return {
      score: dto?.score ?? null,
      band: toPriorityBand(dto?.band ?? null),
      rawBand: dto?.band ?? null,
      impactShare: dto?.impactShare ?? null,
      floorApplied: Boolean(dto?.floorApplied),
    };
  }

  static fromGroundTruthApi(dto: GapGroundTruthDto): GapGroundTruth {
    return {
      ourDraft: dto.ourDraft,
      humanSent: dto.humanSent,
      bucket: dto.bucket,
      similarity: dto.similarity,
      bucketReason: dto.bucketReason,
      signalHoursAfterTrace: dto.signalHoursAfterTrace,
      signalActor: dto.signalActor,
      matchedThisDraft: dto.matchedThisDraft,
    };
  }

  /**
   * List rows carry no ground truth — only the detail endpoint returns runs,
   * which `fromDetailApi` joins on afterwards.
   */
  static fromTraceRefApi(dto: GapTraceRefDto): GapTraceRef {
    return {
      traceId: dto.traceId,
      runId: dto.runId,
      at: dto.at,
      bucket: dto.bucket,
      conversationId: dto.conversationId ?? null,
      latestMessageId: dto.latestMessageId ?? null,
      // Always the backend's absolute URL — the board never assembles one.
      // Null when the account has no configured Langfuse project id.
      langfuseUrl: dto.langfuseUrl,
      ticketUrl: dto.ticketUrl ?? null,
      groundTruth: null,
    };
  }

  /**
   * Folds the detail endpoint's parallel `runs` array onto the gap's trace
   * refs, matched by `traceId`. The run is authoritative for both the frozen
   * ground-truth excerpts and the absolute Langfuse URL.
   */
  private static joinRuns(traceRefs: GapTraceRef[], runs: GapRunDto[]): GapTraceRef[] {
    const runsByTraceId = new Map(runs.map((run) => [run.traceId, run]));
    return traceRefs.map((traceRef) => {
      const run = runsByTraceId.get(traceRef.traceId);
      if (!run) return traceRef;
      return {
        ...traceRef,
        langfuseUrl: run.langfuseUrl,
        ticketUrl: run.ticketUrl ?? null,
        groundTruth: run.groundTruth ? this.fromGroundTruthApi(run.groundTruth) : null,
      };
    });
  }

  static fromStatusEventApi(dto: GapStatusEventDto): GapStatusEvent {
    return {
      status: toGapStatus(dto.status),
      rawStatus: dto.status,
      at: dto.at,
      by: dto.by,
    };
  }

  static fromActionApi(dto: GapActionDto): GapAction {
    return {
      id: dto.id,
      gapId: dto.gapId,
      findingKey: dto.findingKey,
      type: ACTION_TYPES.includes(dto.type as GapActionType) ? (dto.type as GapActionType) : null,
      rawType: dto.type,
      text: dto.text,
      user: dto.user ? { id: dto.user.id, email: dto.user.email, name: dto.user.name } : null,
      createdAt: dto.createdAt,
      ingested: dto.ingested,
      isOptimistic: false,
      fixId: dto.fixId ?? null,
    };
  }

  static fromActionApiList(dtos: GapActionDto[]): GapAction[] {
    return dtos.map((dto) => this.fromActionApi(dto));
  }

  /**
   * True when not one trace ref carries a recorded human reply, so the verdict
   * rests on trace evidence alone. Only meaningful after `joinRuns` — list rows
   * carry no ground truth at all.
   */
  private static deriveIsUngrounded(traceRefs: GapTraceRef[]): boolean {
    return traceRefs.length > 0 && !traceRefs.some((ref) => hasOutcomeEvidence(ref));
  }

  static fromApi(dto: GapDto): Gap {
    const traceRefs = Array.isArray(dto.traceRefs)
      ? dto.traceRefs.map((ref) => this.fromTraceRefApi(ref))
      : [];
    return {
      id: dto.id,
      displayId: dto.displayId ?? null,
      findingKey: dto.findingKey,
      accountId: dto.accountId,
      slug: dto.slug,
      title: dto.title,
      description: dto.description,
      status: toGapStatus(dto.status),
      rawStatus: dto.status,
      firstBrokenRung: toLadderRung(dto.firstBrokenRung),
      rootCauseCategory: dto.rootCauseCategory,
      expected: dto.expected ?? null,
      actual: dto.actual ?? null,
      causeChain: dto.causeChain ?? [],
      fixKeys: dto.fixKeys ?? [],
      suggestedFixes: GapAdapter.fromSuggestedFixesApi(dto.suggestedFixes),
      // The board labels this "Impacted tickets", per the Figma design and the
      // recorded plan (decision #9). The field keeps the backend's name because
      // that is the wire contract and the API's sort key; it counts distinct
      // impacted conversations (tickets), not individual AI message turns.
      impactedTickets: dto.impactedTickets ?? 0,
      intents: dto.intents ?? [],
      traceRefs,
      reporters: dto.reporters ?? [],
      severity: this.fromSeverityApi(dto.severity),
      priority: this.fromPriorityApi(dto.priority),
      statusHistory: (dto.statusHistory ?? []).map((event) => this.fromStatusEventApi(event)),
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt,
    };
  }

  static fromApiList(dtos: GapDto[]): Gap[] {
    return dtos.map((dto) => this.fromApi(dto));
  }

  static fromListApi(dto: GapListDto): GapPage {
    return {
      items: this.fromApiList(dto.gaps ?? []),
      total: dto.count,
      limit: dto.limit,
      offset: dto.offset,
    };
  }

  /** Drops anything that isn't a non-blank name, so the filter never lists an empty option. */
  static fromIntentsApi(dto: GapIntentsDto | null | undefined): string[] {
    const intents: unknown[] = Array.isArray(dto?.intents) ? dto.intents : [];
    return intents.filter((intent): intent is string => typeof intent === 'string' && intent.trim() !== '');
  }

  /**
   * A count is only usable if it is a finite, non-negative number. Anything
   * else on the wire — absent, null, a string, NaN, a negative — reads as
   * "the server did not tell us", so the board falls back to counting the
   * fixes itself rather than gating a resolve on a wrong number.
   */
  private static toFixCount(value: unknown): number | null {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
    return value;
  }

  static fromDetailApi(dto: GapDetailDto): GapDetail {
    const gap = this.fromApi(dto.gap);
    const traceRefs = this.joinRuns(gap.traceRefs, dto.runs ?? []);
    return {
      ...gap,
      traceRefs,
      isUngrounded: this.deriveIsUngrounded(traceRefs),
      actions: this.fromActionApiList(dto.actions ?? []),
      unaddressedFixCount: this.toFixCount(dto.gap.unaddressedFixCount),
    };
  }

  // ── Ticket thread ─────────────────────────────────────────────────────────

  static fromThreadMessageApi(dto: GapThreadMessageDto): GapThreadMessage {
    return { messageId: dto.messageId, content: dto.content ?? '', createdAt: dto.createdAt };
  }

  static fromThreadFeedbackApi(dto: GapThreadFeedbackDto): GapThreadFeedback {
    return {
      feedbackId: dto.feedbackId,
      aiEventLogId: dto.aiEventLogId,
      revisionTarget: dto.revisionTarget ?? null,
      verdict: dto.verdict,
      reasons: dto.reasons ?? [],
      otherText: dto.otherText,
      rewrite: dto.rewrite,
      addressed: Boolean(dto.addressed),
      createdAt: dto.createdAt,
    };
  }

  static fromThreadRefineApi(dto: GapThreadRefineDto | null | undefined): GapThreadRefine | null {
    if (!dto) return null;
    return { note: dto.note ?? null, command: dto.command ?? null, requestedAt: dto.requestedAt ?? null };
  }

  static fromThreadBlueprintApi(dto: GapThreadBlueprintDto): GapThreadBlueprint {
    return {
      aiEventLogId: dto.aiEventLogId,
      revisionNumber: dto.revisionNumber ?? null,
      cause: toRevisionCause(dto.cause),
      steps: (dto.steps ?? []).map((step) => ({
        stepId: step.stepId ?? '',
        description: step.description ?? '',
        toolName: step.toolName ?? null,
      })),
      refine: this.fromThreadRefineApi(dto.refine),
      createdAt: dto.createdAt,
    };
  }

  static fromThreadResponseApi(dto: GapThreadResponseDto): GapThreadResponse {
    return {
      aiEventLogId: dto.aiEventLogId,
      content: dto.content ?? '',
      sent: Boolean(dto.sent),
      refine: this.fromThreadRefineApi(dto.refine),
      createdAt: dto.createdAt,
    };
  }

  static fromThreadTurnApi(dto: GapThreadTurnDto): GapThreadTurn {
    return {
      latestMessageId: dto.latestMessageId ?? null,
      isGapTurn: Boolean(dto.isGapTurn),
      customerMessages: (dto.customerMessages ?? []).map((message) => this.fromThreadMessageApi(message)),
      reply: dto.reply ? this.fromThreadMessageApi(dto.reply) : null,
      blueprints: (dto.blueprints ?? []).map((blueprint) => this.fromThreadBlueprintApi(blueprint)),
      responses: (dto.responses ?? []).map((response) => this.fromThreadResponseApi(response)),
      feedbacks: (dto.feedbacks ?? []).map((feedback) => this.fromThreadFeedbackApi(feedback)),
      endState: toTurnEnd(dto.endState),
      endedAt: dto.endedAt ?? null,
    };
  }

  static fromThreadTicketApi(dto: GapThreadTicketDto): GapThreadTicket {
    return {
      conversationId: dto.conversationId,
      sentinelTicketId: dto.sentinelTicketId ?? null,
      traceIds: dto.traceIds ?? [],
      turns: (dto.turns ?? []).map((turn) => this.fromThreadTurnApi(turn)),
      customerEmail: dto.customerEmail ?? null,
      agentName: dto.agentName ?? null,
      status: toTicketStatus(dto.status),
      lastActivityAt: dto.lastActivityAt ?? null,
      externalId: dto.externalId ?? null,
      displayId: dto.displayId ?? null,
      ticketUrl: dto.ticketUrl ?? null,
    };
  }

  static fromThreadApi(dto: GapThreadDto): GapThread {
    return {
      gapId: dto.gapId,
      tickets: (dto.tickets ?? []).map((ticket) => this.fromThreadTicketApi(ticket)),
      ticketCount: dto.ticketCount ?? 0,
      limit: dto.limit,
      offset: dto.offset,
    };
  }

  /** The backend sanitizes too; stripping here keeps markup out of the request. */
  static toActionRequest(payload: GapActionRequestDto): GapActionRequestDto {
    return this.sanitizePayload(payload);
  }

  // ── Testing rail — gap test runs ────────────────────────────────────────

  static fromRunSetTotalsApi(dto: GapRunSetTotalsDto): GapRunSetTotals {
    return {
      planned: dto.planned ?? 0,
      finished: dto.finished ?? 0,
      passed: dto.passed ?? 0,
      failed: dto.failed ?? 0,
      skipped: dto.skipped ?? 0,
      regressions: dto.regressions ?? 0,
      regressionChecks: dto.regressionChecks ?? 0,
    };
  }

  static fromRunSetUserApi(dto: GapRunSetUserDto | null): GapRunSetUser | null {
    if (!dto) return null;
    return { id: dto.id ?? null, email: dto.email ?? null, name: dto.name ?? null };
  }

  static fromRunSetApi(dto: GapRunSetDto): GapRunSet {
    return {
      runSetId: dto.runSetId,
      gapId: dto.gapId,
      status: toRunSetStatus(dto.status),
      rawStatus: dto.status,
      judgeVersion: dto.judgeVersion,
      previousRunSetId: dto.previousRunSetId ?? null,
      totals: this.fromRunSetTotalsApi(dto.totals),
      // Never null on the wire, but the same optional-chaining defence as `fromActionApi`'s user.
      startedBy: this.fromRunSetUserApi(dto.startedBy) ?? { id: null, email: null, name: null },
      startedAt: dto.startedAt ?? null,
      finishedAt: dto.finishedAt ?? null,
      cancelledBy: this.fromRunSetUserApi(dto.cancelledBy),
      updatedAt: dto.updatedAt ?? null,
      resumeCount: dto.resumeCount ?? 0,
    };
  }

  static fromRunSetListApi(dtos: GapRunSetDto[] | null | undefined): GapRunSet[] {
    return (dtos ?? []).map((dto) => this.fromRunSetApi(dto));
  }

  static fromRunRowApi(dto: GapRunRowDto): GapRunRow {
    return {
      caseKind: toRunCaseKind(dto.caseKind),
      traceRefId: dto.traceRefId,
      conversationId: dto.conversationId ?? null,
      latestMessageId: dto.latestMessageId ?? null,
      status: toRunRowStatus(dto.status),
      rawStatus: dto.status,
      simRequestId: dto.simRequestId ?? null,
      simTraceId: dto.simTraceId ?? null,
      draft: dto.draft ?? null,
      humanReply: dto.humanReply ?? null,
      verdict: toRunVerdict(dto.verdict),
      verdictReason: dto.verdictReason ?? null,
      flags: dto.flags ?? [],
      worldRebuilt: Boolean(dto.worldRebuilt),
      errorMessage: dto.errorMessage ?? null,
      startedAt: dto.startedAt ?? null,
      finishedAt: dto.finishedAt ?? null,
      inputSource: toRunRowInputSource(dto.inputSource),
      regressionSource: toRegressionSource(dto.regressionSource),
      relatedGapId: dto.relatedGapId ?? null,
      baselineDraft: dto.baselineDraft ?? null,
      previousDraft: dto.previousDraft ?? null,
      previousSource: toRunRowPreviousSource(dto.previousSource),
    };
  }

  static fromRegressedGapApi(dto: GapRegressedGapDto): GapRegressedGap {
    return {
      gapId: dto.gapId,
      displayId: dto.displayId ?? null,
      title: dto.title,
      status: toGapStatus(dto.status),
      rawStatus: dto.status,
      priorityBand: toPriorityBand(dto.priorityBand),
      rawPriorityBand: dto.priorityBand ?? null,
      regressedTickets: dto.regressedTickets ?? 0,
    };
  }

  static fromRunSetDetailApi(dto: GapRunSetDetailDto): GapRunSetDetail {
    return {
      ...this.fromRunSetApi(dto),
      rows: (dto.rows ?? []).map((row) => this.fromRunRowApi(row)),
      regressedGaps: (dto.regressedGaps ?? []).map((gap) => this.fromRegressedGapApi(gap)),
      regressedPassingTickets: dto.regressedPassingTickets ?? 0,
    };
  }

  /**
   * A live progress frame folded onto a run set already in cache. Status and
   * totals are the only fields a frame is authoritative for; everything else
   * (who started it, the judge version, the previous run) is immutable for the
   * life of the run and is carried through untouched.
   *
   * Generic over the run set so a `GapRunSetDetail` keeps its `rows` — see
   * `withRunRowProgress` for those.
   */
  static withRunSetProgress<T extends GapRunSet>(runSet: T, dto: GapRunProgressDto): T {
    return {
      ...runSet,
      status: toRunSetStatus(dto.status),
      rawStatus: dto.status,
      totals: this.fromRunSetTotalsApi(dto.totals),
    };
  }

  /**
   * A frame's `row` folded onto the matching cached row, so the Tickets
   * section's chips keep moving live without refetching every row's draft
   * text. The frame carries no draft, reason or timestamps — those stay as the
   * last full response left them and are refreshed by the poll.
   *
   * Returns the same array when the frame carries no row, or names one this
   * client has not loaded.
   */
  static withRunRowProgress(rows: GapRunRow[], dto: GapRunProgressDto): GapRunRow[] {
    const frameRow = dto.row;
    if (!frameRow) return rows;
    if (!rows.some((row) => row.traceRefId === frameRow.traceRefId)) return rows;

    return rows.map((row) =>
      row.traceRefId === frameRow.traceRefId
        ? {
            ...row,
            status: toRunRowStatus(frameRow.status),
            rawStatus: frameRow.status,
            verdict: toRunVerdict(frameRow.verdict),
            flags: frameRow.flags ?? [],
          }
        : row
    );
  }
}
